// frontend/src/components/modals/DataExportModal.jsx
// Data export modal with format selection and progress

import React, { useState } from 'react';
import { FiDownload, FiX, FiFile, FiDatabase, FiArchive } from 'react-icons/fi';
import api from '../../services/api';

export default function DataExportModal({ onClose, onExport }) {
  const [selectedFormats, setSelectedFormats] = useState(['json']);
  const [includeData, setIncludeData] = useState({
    profile: true,
    quiz_history: true,
    achievements: true,
    audit_log: false,
    preferences: true
  });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const formats = [
    { id: 'json', name: 'JSON', icon: FiFile, description: 'Machine-readable format' },
    { id: 'csv', name: 'CSV', icon: FiDatabase, description: 'Spreadsheet format' },
    { id: 'zip', name: 'ZIP Archive', icon: FiArchive, description: 'All formats in one file' }
  ];

  const dataTypes = [
    { key: 'profile', name: 'Profile Information', description: 'Personal details and settings' },
    { key: 'quiz_history', name: 'Quiz History', description: 'All quiz attempts and scores' },
    { key: 'achievements', name: 'Achievements', description: 'Earned badges and progress' },
    { key: 'audit_log', name: 'Audit Log', description: 'Account activity history' },
    { key: 'preferences', name: 'Preferences', description: 'App settings and customizations' }
  ];

  const handleFormatToggle = (formatId) => {
    setSelectedFormats(prev => 
      prev.includes(formatId) 
        ? prev.filter(f => f !== formatId)
        : [...prev, formatId]
    );
  };

  const handleDataToggle = (dataKey) => {
    setIncludeData(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const handleExport = async () => {
    setExporting(true);
    setExportProgress(0);
    
    try {
      // Simulate progress for demo - replace with actual API call
      const interval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await api.post('/users/me/export', {
        formats: selectedFormats,
        data_types: Object.keys(includeData).filter(key => includeData[key])
      });

      clearInterval(interval);
      setExportProgress(100);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = response.data.download_url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        onExport(selectedFormats);
      }, 1000);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
      setExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content data-export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Export Your Data</h3>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {!exporting ? (
            <>
              <div className="export-section">
                <h4>Select Format</h4>
                <div className="formats-grid">
                  {formats.map(format => (
                    <label key={format.id} className="format-option">
                      <input
                        type="checkbox"
                        checked={selectedFormats.includes(format.id)}
                        onChange={() => handleFormatToggle(format.id)}
                      />
                      <div className="format-card">
                        <format.icon size={24} />
                        <h5>{format.name}</h5>
                        <p>{format.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="export-section">
                <h4>Include Data</h4>
                <div className="data-types-list">
                  {dataTypes.map(dataType => (
                    <label key={dataType.key} className="data-type-option">
                      <input
                        type="checkbox"
                        checked={includeData[dataType.key]}
                        onChange={() => handleDataToggle(dataType.key)}
                      />
                      <div className="data-type-info">
                        <h5>{dataType.name}</h5>
                        <p>{dataType.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="export-note">
                <p><strong>Note:</strong> Your data will be prepared and downloaded as a file. This process may take a few moments depending on the amount of data.</p>
              </div>

              <div className="export-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleExport}
                  disabled={selectedFormats.length === 0 || Object.values(includeData).every(v => !v)}
                >
                  <FiDownload size={16} />
                  Export Data
                </button>
              </div>
            </>
          ) : (
            <div className="export-progress">
              <div className="progress-icon">
                <FiDownload size={48} />
              </div>
              <h4>Preparing Your Export</h4>
              <p>Please wait while we gather your data...</p>
              
              <div className="progress-bar-large">
                <div 
                  className="progress-fill" 
                  style={{ width: `${exportProgress}%` }}
                ></div>
              </div>
              <div className="progress-text">{exportProgress}%</div>
              
              {exportProgress === 100 && (
                <p className="success-message">Download should start automatically!</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}